import React, { useState, useEffect } from "react"
import { VStack, Input, Text } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "../utils/toast"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"

interface OrderNameEditorProps {
  order: gqlTypes.Order
  onSuccess: () => void | Promise<void>
  onCancel: () => void
  onBack?: () => void
}

const OrderNameEditor: React.FC<OrderNameEditorProps> = ({ order, onSuccess, onCancel, onBack }) => {
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState<string>(order.name || "")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.ORDER_ID]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_ID],
    [COPY_KEYS.MESSAGES.UPDATED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UPDATED],
    [COPY_KEYS.MESSAGES.UPDATE_FAILED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.UPDATE_FAILED],
    [COPY_KEYS.MESSAGES.VALIDATION_ERROR]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.MESSAGES.VALIDATION_ERROR],
    [COPY_KEYS.FORMS.ENTER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.FORMS.ENTER],
    [COPY_KEYS.ACTIONS.UPDATING]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.ACTIONS.UPDATING],
  })

  // Update state when order changes (e.g., when dialog is reopened with different order)
  useEffect(() => {
    setName(order.name || "")
  }, [order.id, order.name])

  const [updateOrder, { loading }] = useMutationWithEndpoint(gqlTypes.UpdateOrderDocument, undefined, "primary")

  const handleSubmit = async (): Promise<boolean> => {
    let toastId: string | undefined
    try {
      setError(null)

      const trimmedName = name.trim()

      // Validation
      if (!trimmedName) {
        const errorMessage = `${copy[COPY_KEYS.HEADINGS.ORDER_ID]} cannot be empty`
        setError(errorMessage)
        showErrorToast(copy[COPY_KEYS.MESSAGES.VALIDATION_ERROR], errorMessage)
        return false
      }

      toastId = showLoadingToast(`${copy[COPY_KEYS.ACTIONS.UPDATING]} ${copy[COPY_KEYS.HEADINGS.ORDER_ID]}...`)

      const { data: updateData } = await updateOrder({
        variables: {
          id: order.id,
          name: trimmedName,
        },
      })

      if (updateData?.updateOrder) {
        dismissToast(toastId)
        showSuccessToast(`${copy[COPY_KEYS.HEADINGS.ORDER_ID]} ${copy[COPY_KEYS.MESSAGES.UPDATED]} successfully`)
        await onSuccess()
        return true
      } else {
        setError(`Failed to update ${copy[COPY_KEYS.HEADINGS.ORDER_ID]}`)
        showErrorToast(`Failed to update ${copy[COPY_KEYS.HEADINGS.ORDER_ID]}`)
        return false
      }
    } catch (error: any) {
      console.error("Error updating order ID:", error)
      if (toastId) {
        dismissToast(toastId)
      }

      // Check for duplicate name error (409 Conflict)
      const errorMessage = error?.message || ""
      const statusCode = error?.networkError?.statusCode || error?.graphQLErrors?.[0]?.extensions?.code
      const responseBody = error?.networkError?.result?.detail || error?.networkError?.bodyText || ""

      // Check if it's a duplicate error (409 or message contains "already exists" or "Conflict")
      if (
        statusCode === 409 ||
        errorMessage.includes("already exists") ||
        errorMessage.includes("Conflict") ||
        responseBody.includes("already exists")
      ) {
        const duplicateError = `An ${copy[
          COPY_KEYS.HEADINGS.ORDER
        ].toLowerCase()} with this ID already exists. Please choose a different ID.`
        setError(duplicateError)
        showErrorToast(`An ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} with this ID already exists`)
      } else {
        // Show user-friendly error message
        setError(`Unable to update ${copy[COPY_KEYS.HEADINGS.ORDER_ID]}. Please try again.`)
        showErrorToast(
          copy[COPY_KEYS.MESSAGES.UPDATE_FAILED],
          `Unable to update ${copy[COPY_KEYS.HEADINGS.ORDER_ID]}. Please try again.`
        )
      }
      return false
    }
  }

  return (
    <Form onBack={onCancel} actionType="confirm" onSubmit={handleSubmit} loading={loading}>
      <VStack gap={4} align="stretch">
        {error && <ErrorAlert title="Error" message={error} />}

        <Text fontSize="sm">Update the {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} identifier.</Text>

        <Field label={copy[COPY_KEYS.HEADINGS.ORDER_ID]} required>
          <Input
            name="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`${copy[COPY_KEYS.FORMS.ENTER]} ${copy[COPY_KEYS.HEADINGS.ORDER_ID].toLowerCase()}`}
          />
        </Field>
      </VStack>
    </Form>
  )
}

export default OrderNameEditor
